import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  Briefcase,
  FileText,
  Loader2,
  Mail,
  Receipt,
  Search,
  Sparkles,
  User as UserIcon,
  Wallet,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { visibleNavItems } from './navItems'
import { api, ApiError } from '../lib/api'
import type {
  AiStatusResponse,
  SearchHit,
  SearchHitType,
  SearchResponse,
} from '@toga/shared'

const DEBOUNCE_MS = 220

const GROUP_LABEL_KEY: Record<keyof SearchResponse['groups'], string> = {
  clients: 'globalSearch.groups.clients',
  opportunities: 'globalSearch.groups.opportunities',
  documents: 'globalSearch.groups.documents',
  emails: 'globalSearch.groups.emails',
  invoices: 'globalSearch.groups.invoices',
  expenses: 'globalSearch.groups.expenses',
}

const ICONS: Record<SearchHitType, typeof Briefcase> = {
  CLIENT: UserIcon,
  OPPORTUNITY: Briefcase,
  DOCUMENT: FileText,
  EMAIL: Mail,
  INVOICE: Receipt,
  EXPENSE: Wallet,
}

/** Quita tildes y baja a minúsculas, para que "diario" encuentre "Diario". */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

interface Props {
  isLight: boolean
}

interface SemanticHit {
  documentId: string
  documentName: string
  content: string
  pageStart: number | null
  pageEnd: number | null
  score: number
  opportunityId: string | null
  opportunityCode: string | null
}

interface SemanticResponse {
  query: string
  hits: SemanticHit[]
}

type Mode = 'lexical' | 'semantic'

function snippetOf(content: string, q: string, max = 180): string {
  const normalized = content.replace(/\s+/g, ' ').trim()
  if (normalized.length <= max) return normalized
  const lower = normalized.toLowerCase()
  const idx = lower.indexOf(q.toLowerCase())
  if (idx < 0) return normalized.slice(0, max) + '…'
  const start = Math.max(0, idx - 40)
  const end = Math.min(normalized.length, start + max)
  return (start > 0 ? '…' : '') + normalized.slice(start, end) + (end < normalized.length ? '…' : '')
}

export default function GlobalSearch({ isLight }: Props) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const auth = useAuth()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SearchResponse | null>(null)
  const [semantic, setSemantic] = useState<SemanticResponse | null>(null)
  const [mode, setMode] = useState<Mode>('lexical')
  const [ragEnabled, setRagEnabled] = useState<boolean>(false)
  const [ragNotice, setRagNotice] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Comprobamos disponibilidad de RAG una vez al montar.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const s = await api.get<AiStatusResponse>('/ai/status')
        if (!cancelled) setRagEnabled(Boolean(s.ragEnabled))
      } catch {
        if (!cancelled) setRagEnabled(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Debounce de la query — dispara léxica o semántica según `mode`.
  useEffect(() => {
    if (q.trim().length < 2) {
      setData(null)
      setSemantic(null)
      setRagNotice(null)
      return
    }
    const ctrl = new AbortController()
    const timeoutId = window.setTimeout(async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({ q: q.trim() })
        if (mode === 'semantic' && ragEnabled) {
          try {
            const resp = await api.get<SemanticResponse>(
              `/search/semantic?${params.toString()}&limit=20`
            )
            if (!ctrl.signal.aborted) {
              setSemantic(resp)
              setData(null)
              setRagNotice(null)
            }
          } catch (err) {
            const apiErr = err as ApiError
            const code = (apiErr.body as { code?: string } | undefined)?.code
            if (apiErr.status === 503 && code === 'RAG_NOT_CONFIGURED') {
              // Caída a léxica con aviso.
              setMode('lexical')
              setRagEnabled(false)
              setRagNotice(t('globalSearch.ragFallbackNotice'))
              const lex = await api.get<SearchResponse>(`/search?${params.toString()}`)
              if (!ctrl.signal.aborted) {
                setData(lex)
                setSemantic(null)
              }
            } else {
              throw err
            }
          }
        } else {
          const resp = await api.get<SearchResponse>(`/search?${params.toString()}`)
          if (!ctrl.signal.aborted) {
            setData(resp)
            setSemantic(null)
          }
        }
      } catch {
        if (!ctrl.signal.aborted) {
          setData(null)
          setSemantic(null)
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timeoutId)
      ctrl.abort()
    }
  }, [q, mode, ragEnabled])

  // Click fuera para cerrar
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Shortcut Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        setOpen(true)
      } else if (e.key === 'Escape') {
        setOpen(false)
        inputRef.current?.blur()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const onPick = useCallback(
    (hit: SearchHit) => {
      setOpen(false)
      setQ('')
      navigate(hit.href)
    },
    [navigate]
  )

  const onPickSemantic = useCallback(
    (hit: SemanticHit) => {
      setOpen(false)
      setQ('')
      navigate(hit.opportunityId ? `/opportunities/${hit.opportunityId}` : '/documents')
    },
    [navigate]
  )

  // Bloque "Ir a…": sale del MISMO árbol que pinta el sidebar, filtrado por el
  // rol de quien busca. Se resuelve en cliente, sin ir al servidor: es lo que
  // permite que teclear "diario" y pulsar Enter sean cero clics de ratón.
  const navMatches = (() => {
    const needle = fold(q.trim())
    if (needle.length < 2) return []
    return visibleNavItems(role)
      .map((it) => ({ ...it, text: t(it.labelKey, it.label) }))
      .filter((it) => fold(it.text).includes(needle) || fold(it.group).includes(needle))
      .slice(0, 6)
  })()

  const groups = data?.groups
  const lexicalEmpty =
    mode === 'lexical' &&
    !loading &&
    q.trim().length >= 2 &&
    groups &&
    groups.clients.length === 0 &&
    groups.opportunities.length === 0 &&
    groups.documents.length === 0 &&
    groups.emails.length === 0 &&
    groups.invoices.length === 0 &&
    groups.expenses.length === 0 &&
    navMatches.length === 0
  const semanticEmpty =
    mode === 'semantic' &&
    !loading &&
    q.trim().length >= 2 &&
    semantic &&
    semantic.hits.length === 0

  return (
    <div className="relative" ref={containerRef}>
      <Search
        size={16}
        className={[
          'absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none',
          isLight ? 'text-slate-400' : 'text-slate-500',
        ].join(' ')}
      />
      <input
        ref={inputRef}
        type="text"
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder={
          mode === 'semantic'
            ? t('globalSearch.placeholderSemantic')
            : t('globalSearch.placeholderLexical')
        }
        className={[
          'w-96 pl-9 pr-12 py-2 text-sm rounded-full focus:outline-none focus:ring-1 focus:ring-toga-500/40 transition',
          isLight
            ? 'bg-white/80 border border-slate-200 text-slate-700 placeholder-slate-400 focus:border-toga-500/60'
            : 'bg-slate-800/60 border border-slate-700/60 text-slate-700 dark:text-slate-200 placeholder-slate-500 focus:border-toga-500/60',
        ].join(' ')}
      />
      <kbd
        className={[
          'absolute right-3 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 rounded border pointer-events-none',
          isLight
            ? 'border-slate-200 text-slate-400'
            : 'border-slate-700 text-slate-500 bg-slate-900/50',
        ].join(' ')}
      >
        ⌘K
      </kbd>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute left-0 right-0 mt-2 w-[28rem] max-h-[70vh] overflow-y-auto surface-deep p-2 shadow-xl animate-slideUp z-50"
          style={{ background: 'var(--bg-surface-strong)' }}
        >
          {/* Toggle léxica/semántica */}
          <div className="flex items-center justify-between gap-2 px-2 pb-2 mb-1 border-b border-white/5">
            <div className="inline-flex rounded-full border border-white/10 overflow-hidden text-[11px]">
              <button
                type="button"
                onClick={() => setMode('lexical')}
                className={[
                  'px-3 py-1 transition',
                  mode === 'lexical' ? 'bg-toga-500/20 t-primary' : 't-muted hover:bg-hover',
                ].join(' ')}
              >
                {t('globalSearch.modeLexical')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (ragEnabled) setMode('semantic')
                }}
                disabled={!ragEnabled}
                title={ragEnabled ? t('globalSearch.ragEnabledTitle') : t('globalSearch.ragNotConfiguredTitle')}
                className={[
                  'px-3 py-1 transition inline-flex items-center gap-1',
                  mode === 'semantic' ? 'bg-toga-500/20 t-primary' : 't-muted hover:bg-hover',
                  !ragEnabled ? 'opacity-50 cursor-not-allowed' : '',
                ].join(' ')}
              >
                <Sparkles size={11} /> {t('globalSearch.modeSemantic')}
              </button>
            </div>
            {ragNotice && (
              <div className="text-[10px] t-faint truncate">{ragNotice}</div>
            )}
          </div>

          {loading && (
            <div className="flex items-center gap-2 px-3 py-3 text-xs t-muted">
              <Loader2 size={12} className="animate-spin" /> {t('globalSearch.searching')}
            </div>
          )}
          {(lexicalEmpty || semanticEmpty) && !loading && (
            <div className="px-3 py-4 text-xs t-faint text-center">{t('globalSearch.noResults')}</div>
          )}

          {/* "Ir a…" — va PRIMERO y no espera al servidor: es la vía directa. */}
          {mode === 'lexical' && navMatches.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-wider t-faint px-3 py-1">
                {t('globalSearch.groups.navigate')}
              </div>
              <ul>
                {navMatches.map((it) => {
                  const Icon = it.icon
                  return (
                    <li key={it.to}>
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false)
                          setQ('')
                          if (it.external) window.open(it.to, '_blank', 'noopener')
                          else navigate(it.to)
                        }}
                        className="w-full text-left px-3 py-2 rounded hover:bg-hover flex items-center gap-2"
                      >
                        <Icon size={14} className="t-faint shrink-0" />
                        <span className="text-sm t-primary flex-1 truncate">{it.text}</span>
                        <span className="text-[10px] t-faint">{it.group}</span>
                        <ArrowRight size={12} className="t-faint shrink-0" />
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Resultados léxicos */}
          {mode === 'lexical' && groups && !loading && !lexicalEmpty && (
            <>
              {(['opportunities', 'clients', 'invoices', 'expenses', 'documents', 'emails'] as const).map((key) => {
                const items = groups[key]
                if (items.length === 0) return null
                return (
                  <div key={key} className="mb-2 last:mb-0">
                    <div className="text-[10px] uppercase tracking-wider t-faint px-3 py-1">
                      {t(GROUP_LABEL_KEY[key])}
                    </div>
                    <ul>
                      {items.map((hit) => {
                        const Icon = ICONS[hit.type]
                        return (
                          <li key={`${hit.type}-${hit.id}`}>
                            <button
                              type="button"
                              onClick={() => onPick(hit)}
                              className="w-full text-left px-3 py-2 rounded hover:bg-hover flex items-start gap-2"
                            >
                              <Icon size={14} className="t-faint mt-0.5 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm t-primary truncate">{hit.title}</div>
                                {hit.subtitle && (
                                  <div className="text-[11px] t-faint truncate">
                                    {hit.subtitle}
                                  </div>
                                )}
                                {hit.snippet && (
                                  <div className="text-[11px] t-muted truncate mt-0.5">
                                    {hit.snippet}
                                  </div>
                                )}
                              </div>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )
              })}
            </>
          )}

          {/* Resultados semánticos */}
          {mode === 'semantic' && semantic && !loading && !semanticEmpty && (
            <div className="mb-1">
              <div className="text-[10px] uppercase tracking-wider t-faint px-3 py-1">
                {t('globalSearch.relevantFragments')}
              </div>
              <ul>
                {semantic.hits.map((hit) => {
                  const pages =
                    hit.pageStart != null
                      ? hit.pageEnd != null && hit.pageEnd !== hit.pageStart
                        ? ` · pp. ${hit.pageStart}-${hit.pageEnd}`
                        : ` · p. ${hit.pageStart}`
                      : ''
                  const subtitle = `${hit.documentName}${
                    hit.opportunityCode ? ` · ${hit.opportunityCode}` : ''
                  }${pages}`
                  return (
                    <li key={`${hit.documentId}-${hit.content.slice(0, 24)}`}>
                      <button
                        type="button"
                        onClick={() => onPickSemantic(hit)}
                        className="w-full text-left px-3 py-2 rounded hover:bg-hover flex items-start gap-2"
                      >
                        <FileText size={14} className="t-faint mt-0.5 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] t-faint truncate">{subtitle}</div>
                          <div className="text-[12px] t-primary mt-0.5 line-clamp-3">
                            {snippetOf(hit.content, q.trim())}
                          </div>
                          <div className="text-[10px] t-faint mt-1">
                            {t('globalSearch.score')} {hit.score.toFixed(2)}
                          </div>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
